import type { SourceResult, DestinationResult } from '@/services/auth/types';
import type { ServicesApiPipelinesConnection } from '@/services/api/services-api';
import { getIntegrationTypeName, type IntegrationCatalog } from './integration-catalog';
import { filterBySearchTerms } from './search-filter';

/**
 * Maps a ServicesApiPipelinesConnection to a SourceResult or DestinationResult.
 * The `path` field from the API is used as the navigation URL.
 */
export function mapPipelinesConnectionToSearchResult(
  conn: ServicesApiPipelinesConnection,
): SourceResult | DestinationResult {
  const other = {
    type: conn.type,
    enabled: conn.enabled,
    status: conn.status,
    created_at: conn.created_at > 0 ? conn.created_at * 1000 : undefined,
    last_activity: conn.last_activity > 0 ? conn.last_activity * 1000 : undefined,
  };
  return conn.direction === 'Source'
    ? { kind: 'source', doc_id: conn.id, title: conn.name, url: conn.path, other }
    : { kind: 'destination', doc_id: conn.id, title: conn.name, url: conn.path, other };
}

/**
 * Fuzzy-matches connections against the query by title, type slug, and catalog display name,
 * then caps the result set. Capping after filtering avoids hiding later-indexed connections
 * (e.g. destinations on workspaces with many sources, since the API returns connections
 * grouped by direction).
 */
export function filterAndCapIntegrations(
  connections: readonly (SourceResult | DestinationResult)[],
  query: string,
  catalog: IntegrationCatalog,
  cap: number,
): (SourceResult | DestinationResult)[] {
  return filterBySearchTerms(connections, query, {
    cap,
    getTerms: (result) => [result.title, result.other.type, getIntegrationTypeName(result.other.type, catalog) ?? ''],
  });
}
